import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Clock, Package, CheckCircle, Coffee, ChefHat, 
  RotateCcw, ArrowLeft, ChevronDown, ChevronUp,
  ShoppingBag, Star, Calendar, XCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { api } from '@/utils/apiClient';
import { toast } from 'sonner';

const statusConfig = {
  placed: { icon: Package, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
  pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-100 dark:bg-amber-900/30' },
  confirmed: { icon: CheckCircle, color: 'text-teal-500', bg: 'bg-teal-100 dark:bg-teal-900/30' },
  preparing: { icon: ChefHat, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30' },
  ready: { icon: Coffee, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30' },
  completed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30' },
  cancelled: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' }
};

function OrderCard({ order, onReorder }) {
  const [expanded, setExpanded] = useState(false);
  const StatusIcon = statusConfig[order.status]?.icon || Package;
  const statusColor = statusConfig[order.status]?.color || 'text-gray-500';
  const statusBg = statusConfig[order.status]?.bg || 'bg-gray-100';

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl border border-border overflow-hidden"
    >
      {/* Order Header */}
      <div 
        className="p-4 cursor-pointer hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full ${statusBg} flex items-center justify-center`}>
              <StatusIcon className={`w-5 h-5 ${statusColor}`} />
            </div>
            <div>
              <p className="font-medium text-foreground">
                Order #{order.id?.slice(0, 8).toUpperCase()}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatDate(order.created_at)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="font-bold text-foreground">${order.total?.toFixed(2)}</p>
              <p className={`text-xs font-medium ${statusColor}`}>
                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </p>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-border pt-4">
          {/* Items List */}
          <div className="space-y-2 mb-4">
            <p className="text-sm font-medium text-muted-foreground">Items</p>
            {order.items?.map((item, idx) => (
              <div key={idx} className="flex justify-between text-sm">
                <span className="text-foreground">
                  {item.name} {item.size && `(${item.size})`} x{item.quantity}
                </span>
                <span className="text-muted-foreground">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* Status Timeline */}
          {order.timeline && order.timeline.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-muted-foreground mb-3">Status Timeline</p>
              <div className="relative pl-6">
                {order.timeline.map((event, idx) => {
                  const EventIcon = statusConfig[event.status]?.icon || CheckCircle;
                  const eventColor = statusConfig[event.status]?.color || 'text-gray-400';
                  const isLast = idx === order.timeline.length - 1;
                  
                  return (
                    <div key={idx} className="relative pb-4 last:pb-0">
                      {/* Timeline line */}
                      {!isLast && (
                        <div className="absolute left-[-18px] top-6 w-0.5 h-full bg-border" />
                      )}
                      {/* Timeline dot */}
                      <div className={`absolute left-[-24px] top-1 w-3 h-3 rounded-full ${
                        isLast ? 'bg-primary' : 'bg-muted-foreground/30'
                      }`} />
                      <div className="flex items-start gap-2">
                        <EventIcon className={`w-4 h-4 ${eventColor} mt-0.5`} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{event.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(event.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reorder Button */}
          {order.can_reorder && (
            <Button
              onClick={() => onReorder(order)}
              variant="outline"
              className="w-full rounded-full"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reorder
            </Button>
          )}
        </div>
      )}
    </motion.div>
  );
}

function FavoriteItem({ item, onAddToCart }) {
  return (
    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
          <Star className="w-5 h-5 text-primary" />
        </div>
        <div>
          <p className="font-medium text-foreground">{item.name}</p>
          <p className="text-xs text-muted-foreground">
            Ordered {item.order_count} time{item.order_count > 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <p className="font-bold text-foreground">${item.price?.toFixed(2)}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => onAddToCart(item)}
          className="rounded-full"
        >
          Add
        </Button>
      </div>
    </div>
  );
}

export default function OrderHistoryPage() {
  const { user, isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    if (user?.id) {
      fetchOrderHistory();
      fetchFavorites();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, navigate]);

  const fetchOrderHistory = async (skip = 0) => {
    try {
      const res = await api.get(`/api/orders/history/${user.id}?limit=10&skip=${skip}`);
      const data = res.data;
      
      if (skip === 0) {
        setOrders(data.orders || []);
      } else {
        setOrders(prev => [...prev, ...(data.orders || [])]);
      }
      setHasMore(data.has_more || false);
    } catch (err) {
      console.error('Failed to fetch order history:', err);
      toast.error('Failed to load order history');
    }
    setLoading(false);
    setLoadingMore(false);
  };

  const fetchFavorites = async () => {
    try {
      const res = await api.get(`/api/orders/favorites/${user.id}?limit=5`);
      const data = res.data;
      setFavorites(data.favorites || []);
    } catch (err) {
      console.error('Failed to fetch favorites:', err);
    }
  };

  const handleReorder = async (order) => {
    try {
      const res = await api.post(`/api/orders/reorder/${order.id}?user_id=${user.id}`);
      const data = res.data;
      
      if (data.success && data.cart_items) {
        // Add all items to cart
        for (const item of data.cart_items) {
          addToCart(
            { id: item.id, name: item.name, price: item.price, image: '' },
            item.size,
            null,
            item.special_instructions || '',
            null,
            null,
            null
          );
          // If quantity > 1, update the quantity
          // Note: addToCart adds 1 item, so we need to handle quantity differently
        }
        toast.success('Items added to cart!');
        navigate('/order');
      } else {
        toast.error('Failed to reorder');
      }
    } catch (err) {
      console.error('Reorder failed:', err);
      toast.error('Failed to reorder');
    }
  };

  const handleAddFavorite = (item) => {
    addToCart(
      { id: item.item_id, name: item.name, price: item.price, image: '' },
      item.size,
      null,
      '',
      null,
      null,
      null
    );
    toast.success(`${item.name} added to cart!`);
  };

  const loadMore = () => {
    setLoadingMore(true);
    fetchOrderHistory(orders.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-12">
        <div className="max-w-3xl mx-auto px-4 text-center py-20">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/order">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <div>
            <h1 className="font-heading text-3xl text-foreground">Order History</h1>
            <p className="text-muted-foreground">View past orders and reorder favorites</p>
          </div>
        </div>

        {/* Favorites Section */}
        {favorites.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-xl text-foreground">Your Favorites</h2>
            </div>
            <div className="space-y-2">
              {favorites.map((item, idx) => (
                <FavoriteItem 
                  key={idx} 
                  item={item} 
                  onAddToCart={handleAddFavorite}
                />
              ))}
            </div>
          </div>
        )}

        {/* Orders Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5 text-primary" />
            <h2 className="font-heading text-xl text-foreground">Past Orders</h2>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12 bg-card rounded-2xl border border-border">
              <ShoppingBag className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">No orders yet</p>
              <Link to="/menu">
                <Button className="rounded-full">Browse Menu</Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <OrderCard 
                  key={order.id} 
                  order={order}
                  onReorder={handleReorder}
                />
              ))}

              {/* Load More */}
              {hasMore && (
                <div className="text-center pt-4">
                  <Button
                    variant="outline"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="rounded-full"
                  >
                    {loadingMore ? (
                      <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin mr-2" />
                    ) : null}
                    Load More
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
